import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, channelLogoUrl } from '../api/client';
import type { LiveTvChannel, LiveTvProgram } from '../api/types';
import { useApi } from '../api/useApi';
import AsyncSection from '../components/AsyncSection';
import ProgrammeDialog from '../components/livetv/ProgrammeDialog';
import PlayerOverlay from '../components/player/PlayerOverlay';
import PageHeader from '../components/PageHeader';
import { emiroPalette } from '../theme';

/// How much of the evening is on screen at once, and how wide an hour is drawn.
const HOURS_SHOWN = 4;
const PIXELS_PER_HOUR = 260;
const CHANNEL_COLUMN = 200;
const ROW_HEIGHT = 64;

/// Live television as a guide: what is on, on every channel, from every plugin that provides
/// one. Tuners are not in it - how many a provider has and which of them is busy is the
/// plugin's business, not something a person choosing what to watch should have to think about.
/// "Every channel" as a remembered choice. The account keeps a bouquet id, and no id at all
/// already means "nobody has chosen" - the server's default bouquet - so every channel needs one
/// of its own that no bouquet will ever have.
const ALL_CHANNELS = '00000000-0000-0000-0000-000000000000';

export default function LiveTvPage() {
  const theme = useTheme();
  const { t } = useTranslation();

  // A bouquet, or every channel. Where somebody has not chosen one for themselves, the server's
  // default bouquet is what opens; where there is none of those either, every channel does.
  // A bouquet that has been deleted since it was chosen is quietly forgotten rather than shown
  // as an error for something nobody can see any more.
  const bouquets = useApi(['livetv', 'bouquets'], api.liveTvBouquets);
  const preferences = useApi(['users', 'me', 'preferences'], api.myPreferences);
  const [chosenBouquet, setChosenBouquet] = useState<string>();

  const known = (id: string | null | undefined) =>
    Boolean(id) && (bouquets.data ?? []).some((bouquet) => bouquet.id === id);

  const lastBouquetId = preferences.data?.lastBouquetId;

  const opensOn = lastBouquetId === ALL_CHANNELS
    ? ''
    : known(lastBouquetId)
      ? lastBouquetId!
      : bouquets.data?.find((bouquet) => bouquet.isDefault)?.id ?? '';

  // Every channel is a choice too, and it has no bouquet to be known by.
  const bouquetId = chosenBouquet === '' || known(chosenBouquet) ? chosenBouquet! : opensOn;

  // Asked for only once both answers are in: the guide of every channel is a large request to
  // make and then throw away because the bouquet was known a moment later.
  const ready = bouquets.data !== undefined && preferences.data !== undefined;

  const channels = useApi(['livetv', 'channels', ready ? bouquetId || 'all' : 'waiting'], () =>
    api.liveTvChannels(bouquetId || undefined), { enabled: ready });

  // The choice belongs to the person, not to this browser: it is kept on their account, so the
  // television in the living room opens where the laptop left off. A save that fails costs the
  // memory of it and nothing else, which is not worth interrupting somebody's evening for.
  const chooseBouquet = (value: string) => {
    setChosenBouquet(value);

    if (preferences.data) {
      void api
        .saveMyPreferences({ ...preferences.data, lastBouquetId: value || ALL_CHANNELS })
        .catch(() => undefined);
    }
  };

  // Anchored on the half hour, so the grid lines land where a person expects them.
  const [from, setFrom] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0);

    return now;
  });

  const until = new Date(from.getTime() + HOURS_SHOWN * 3600_000);

  const guide = useApi(
    ['livetv', 'guide', from.toISOString()],
    () => api.liveTvGuide(from, until),
    { staleTime: 60_000 },
  );

  const [opened, setOpened] = useState<{ program: LiveTvProgram; channel: LiveTvChannel }>();
  const [watching, setWatching] = useState<LiveTvChannel>();

  // The server hands the channels out in order: a bouquet in the order somebody put it in, the
  // full list by number with the unnumbered after it. Sorting again here mixed numbers and
  // names, and put a scan's unnumbered "." services above channel 1.
  const sorted = channels.data ?? [];

  const offset = (date: Date) => ((date.getTime() - from.getTime()) / 3600_000) * PIXELS_PER_HOUR;
  const now = new Date();

  // What is on now is filled with the accent. In daylight that teal is deep enough that the
  // page's own text disappears on it, so there the fill brings the text that belongs to it.
  const nowFill =
    theme.palette.mode === 'dark'
      ? { backgroundColor: 'primary.dark' }
      : { backgroundColor: 'primary.main', color: 'primary.contrastText' };

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t('livetv.title')}
        actions={
          <>
            {(bouquets.data ?? []).length > 0 && (
              <TextField
                select
                size="small"
                fullWidth={false}
                label={t('livetv.bouquet')}
                value={bouquetId}
                // "All channels" is the empty value, and a select shows an empty value as
                // nothing at all unless told otherwise - which read as a field nobody had
                // filled in.
                slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                onChange={(event) => chooseBouquet(event.target.value)}
                sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
              >
                <MenuItem value="">{t('livetv.allChannels')}</MenuItem>
                {bouquets.data?.map((bouquet) => (
                  <MenuItem key={bouquet.id} value={bouquet.id}>
                    {bouquet.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Button onClick={() => setFrom(new Date(from.getTime() - HOURS_SHOWN * 3600_000))}>
              {t('livetv.earlier')}
            </Button>
            <Button onClick={() => setFrom(new Date(from.getTime() + HOURS_SHOWN * 3600_000))}>
              {t('livetv.later')}
            </Button>
          </>
        }
      />

      <AsyncSection loading={channels.loading} error={channels.error} onRetry={channels.reload}>
        {sorted.length === 0 ? (
          <Stack spacing={1}>
            <Typography variant="h3">{t('livetv.empty')}</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: '60ch' }}>
              {t('livetv.emptyHint')}
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ overflowX: 'auto', pb: 2 }}>
            <Box sx={{ minWidth: CHANNEL_COLUMN + HOURS_SHOWN * PIXELS_PER_HOUR, position: 'relative' }}>
              {/* The clock across the top, one mark every half hour. */}
              <Stack direction="row" sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <Box sx={{ width: CHANNEL_COLUMN, flexShrink: 0 }} />

                {Array.from({ length: HOURS_SHOWN * 2 }, (_, index) => {
                  const time = new Date(from.getTime() + index * 1800_000);

                  return (
                    <Box
                      key={time.toISOString()}
                      sx={{
                        width: PIXELS_PER_HOUR / 2,
                        flexShrink: 0,
                        borderInlineStart: '1px solid',
                        borderColor: 'divider',
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              {/* Where we are now, if now is on screen. */}
              {now >= from && now <= until && (
                <Box
                  sx={{
                    position: 'absolute',
                    insetInlineStart: CHANNEL_COLUMN + offset(now),
                    top: 24,
                    bottom: 0,
                    width: 2,
                    backgroundColor: emiroPalette.accent,
                    zIndex: 1,
                    pointerEvents: 'none',
                  }}
                />
              )}

              <AsyncSection loading={guide.loading} error={guide.error} onRetry={guide.reload}>
                <Stack>
                  {sorted.map((channel) => {
                    const programs = (guide.data ?? []).filter(
                      (program) => program.channelId === channel.id
                        || program.channelId === channel.guideId,
                    );

                    return (
                      <Stack key={`${channel.providerId}-${channel.id}`} direction="row">
                        <Stack
                          direction="row"
                          spacing={1}
                          onClick={() => setWatching(channel)}
                          sx={{
                            width: CHANNEL_COLUMN,
                            flexShrink: 0,
                            height: ROW_HEIGHT,
                            alignItems: 'center',
                            px: 1,
                            borderBlockEnd: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                        >
                          {channel.logo && (
                            <Box
                              component="img"
                              src={channelLogoUrl(channel.logo, theme.palette.mode)}
                              alt=""
                              sx={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }}
                            />
                          )}

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap>
                              {channel.name}
                            </Typography>
                            {channel.number && (
                              <Typography variant="caption" color="text.secondary">
                                {channel.number}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Box
                          sx={{
                            position: 'relative',
                            flexGrow: 1,
                            height: ROW_HEIGHT,
                            borderBlockEnd: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {programs.length === 0 ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ position: 'absolute', insetInlineStart: 8, top: 20 }}
                            >
                              {t('livetv.noGuide')}
                            </Typography>
                          ) : (
                            programs.map((program) => {
                              const starts = new Date(program.startsAt);
                              const ends = new Date(program.endsAt);
                              const left = Math.max(offset(starts), 0);
                              const width = Math.max(offset(ends) - left, 24);
                              const running = starts <= now && ends > now;

                              return (
                                <Box
                                  key={program.id}
                                  onClick={() => setOpened({ program, channel })}
                                  sx={{
                                    position: 'absolute',
                                    insetInlineStart: left,
                                    top: 4,
                                    width: width - 4,
                                    height: ROW_HEIGHT - 12,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    ...(running ? nowFill : { backgroundColor: 'background.paper' }),
                                    border: '1px solid',
                                    borderColor: running ? 'primary.main' : 'divider',
                                    '&:hover': { borderColor: 'primary.main' },
                                  }}
                                >
                                  <Typography variant="body2" noWrap sx={{ fontWeight: running ? 700 : 400 }}>
                                    {program.title}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    // On the filled block the time reads as a quieter version of
                                    // the title rather than as the page's grey.
                                    color={running ? 'inherit' : 'text.secondary'}
                                    sx={{ opacity: running ? 0.8 : 1 }}
                                  >
                                    {starts.toLocaleTimeString(undefined, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              </AsyncSection>
            </Box>
          </Box>
        )}
      </AsyncSection>

      <ProgrammeDialog
        opened={opened}
        onClose={() => setOpened(undefined)}
        onWatch={(channel) => {
          setOpened(undefined);
          setWatching(channel);
        }}
      />

      {watching && (
        <PlayerOverlay
          target={{ kind: 'live', channel: watching }}
          onClose={() => setWatching(undefined)}
        />
      )}
    </Stack>
  );
}
